import { useMemo } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { getCancellationWaitlistEntries } from '../../services/cancellation-waitlist';
import useUser from '../global/useUser';

const PAGE_SIZE = 25;

function useWaitlist({
  businessId,
  employeeIds,
}: {
  businessId: string | undefined;
  employeeIds: string[];
}) {
  const { user } = useUser();

  const { data, isLoading, isFetching, isError, hasNextPage, fetchNextPage } =
    useInfiniteQuery({
      queryKey: ['cancellation-waitlist', { businessId, employeeIds }],
      queryFn: async ({ pageParam: cursor }) =>
        getCancellationWaitlistEntries({
          businessId: businessId!,
          employeeIds,
          limit: PAGE_SIZE,
          cursor,
        }),
      initialPageParam: undefined as string | undefined,
      getNextPageParam: lastPage => {
        if (!lastPage.meta?.hasMore || !lastPage.meta?.cursor) return undefined;
        return lastPage.meta.cursor;
      },
      enabled: !!user?.userId && !!businessId && employeeIds.length > 0,
    });

  const entries = useMemo(
    () => data?.pages?.flatMap(page => page.entries) ?? [],
    [data?.pages],
  );

  return {
    entries,
    totalCount: data?.pages?.[0]?.meta?.count,
    isLoading,
    isFetching,
    isError,
    hasNextPage,
    fetchNextPage,
  };
}

export default useWaitlist;
